/**
 * TaskProcSafeModal — single-purpose Procedures OR Password Safe modal for
 * a task, sub-task, or company. The `mode` prop picks which; each opens as
 * its OWN modal (no tabs) with its own title.
 *
 * SIZING — the "small modal" rule: 30% of the screen clear around the
 * outside on desktop (all four edges pinned at 30% insets, so geometry is
 * margin-defined and can never overflow); relaxed insets on small screens
 * so it stays usable.
 *
 * PASSWORDS mode:
 *  - Lists entries attached to the task/sub-task (or the company's
 *    entries in company mode) THAT THE CALLER CAN SEE. Hidden entries
 *    never render — not even as placeholders.
 *  - Passwords stay masked until the eye button reveals them; CLICKING the
 *    password (masked or revealed) copies it to the clipboard.
 *  - "New Password Entry" creates an entry in context with scope options:
 *      · This project only
 *      · A company on this project (select when several) — either for
 *        this project only, or company-wide (available to any project
 *        with that company)
 *    Access is chosen with the dual-list picker: available users on the
 *    left, chosen users (who can see the entry) on the right. Until you
 *    add people, it's only you.
 *  - Edit (pencil) opens the same inline form pre-filled for an existing
 *    entry: name, username, password (blank = keep current), URL, type,
 *    scope, notes — and, for the entry's OWNER (server `can_manage`: the
 *    original creator or the principal-owner user), who can see it. Any
 *    user who can see the entry may edit its fields; only the owner may
 *    change the access list or delete it.
 *  - In company mode the new entry is company-scoped (company-wide).
 *
 * PROCEDURES mode: attached procedures with a non-blocking pop-out viewer
 * and an attach picker (task/sub-task), or the company's procedures.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Select, Box, Text, Badge,
  Flex, IconButton, useToast, Tooltip, Spinner, Input, InputGroup,
  InputRightElement, Progress, Radio, RadioGroup, Stack, Textarea, Checkbox,
} from '@chakra-ui/react';
import {
  FiClipboard, FiKey, FiEye, FiEyeOff, FiTrash2, FiExternalLink, FiPlus,
  FiRefreshCw, FiEdit2,
} from 'react-icons/fi';
import api from '../api';
import { ProcedureViewer } from './ProceduresDashboard';
import { passwordQuality, genPassword } from './PasswordSafeDashboard';
import UserTransferList from './UserTransferList';
import { useAuth } from '../contexts/AuthContext';

const qualityColor = (q) => q >= 75 ? 'green' : q >= 50 ? 'yellow' : q >= 25 ? 'orange' : 'red';

// Shared geometry: 30% clear around the outside (desktop), pinned edges.
const smallModalProps = {
  position: 'fixed',
  top: { base: '10%', md: '30%' }, bottom: { base: '10%', md: '30%' },
  left: { base: '5%', md: '30%' }, right: { base: '5%', md: '30%' },
  w: 'auto', maxW: 'none', h: 'auto', maxH: 'none', my: 0,
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

const emptyNewPw = () => ({
  name: '', username: '', password: '', url: '', notes: '',
  scope: 'project',            // 'project' | 'company'
  company_id: '', company_wide: false,
  user_ids: [],                // chosen via transfer list
  entry_type: 'account',       // 'account' | 'sso' (signs in via another entry)
  sso_entry_id: '',
  editing_id: null,            // set when editing an existing entry
  can_manage: true,            // may change the access list (owner)
  pin_id: '', pin_name: '',    // owner pinned in the transfer list
  company_name: '',            // display for a company outside this project's list
});

export default function TaskProcSafeModal({ isOpen, onClose, taskId, subTaskId, companyId, title, mode = 'procedures', onChanged }) {
  const isCompanyMode = !!companyId && !taskId && !subTaskId;
  const { user } = useAuth();
  const [items, setItems] = useState([]);            // attached procedures OR entries
  const [allItems, setAllItems] = useState([]);      // attach picker source
  const [loading, setLoading] = useState(false);
  const [viewProc, setViewProc] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [users, setUsers] = useState([]);
  const [ctx, setCtx] = useState({ project_id: null, project_name: null, companies: [] });
  const [newPw, setNewPw] = useState(null);          // create form state
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [findQ, setFindQ] = useState('');            // search existing to attach/clone
  const [newProc, setNewProc] = useState(null);      // quick-create procedure { name, description, category_id }
  const [procCats, setProcCats] = useState([]);
  const toast = useToast();

  const scopeParam = taskId ? `task_id=${taskId}` : subTaskId ? `sub_task_id=${subTaskId}` : '';
  const isPasswords = mode === 'passwords';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      if (isPasswords) {
        if (isCompanyMode) {
          const { data } = await api.get(`/password-safe?company_id=${companyId}`);
          setItems(Array.isArray(data) ? data : []);
        } else {
          const [att, all] = await Promise.all([
            api.get(`/password-safe/assignments/list?${scopeParam}`).then(r => r.data).catch(() => []),
            api.get('/password-safe').then(r => r.data).catch(() => []),
          ]);
          setItems(Array.isArray(att) ? att : []);
          setAllItems(Array.isArray(all) ? all : []);
        }
      } else {
        if (isCompanyMode) {
          const [own, all] = await Promise.all([
            api.get(`/procedures?company_id=${companyId}`).then(r => r.data).catch(() => []),
            api.get('/procedures').then(r => r.data).catch(() => []),
          ]);
          setItems(Array.isArray(own) ? own : []);
          setAllItems(Array.isArray(all) ? all : []);
        } else {
          const [att, all] = await Promise.all([
            api.get(`/procedures/assignments/list?${scopeParam}`).then(r => r.data).catch(() => []),
            api.get('/procedures').then(r => r.data).catch(() => []),
          ]);
          setItems(Array.isArray(att) ? att : []);
          setAllItems(Array.isArray(all) ? all : []);
        }
      }
    } finally { setLoading(false); }
  }, [isPasswords, isCompanyMode, companyId, scopeParam]);

  useEffect(() => {
    if (!isOpen) return;
    fetchAll();
    setViewProc(null); setRevealed({}); setNewPw(null); setFindQ(''); setNewProc(null);
    if (isPasswords) {
      api.get('/calendar/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      if (!isCompanyMode) {
        api.get(`/password-safe/context/companies?${scopeParam}`)
          .then(r => setCtx(r.data || { companies: [] })).catch(() => {});
      }
    }
  }, [isOpen, fetchAll, isPasswords, isCompanyMode, scopeParam]);

  // ── Attach / detach ──
  const attach = async (id) => {
    if (!id) return;
    try {
      if (isPasswords) await api.post('/password-safe/assignments', { task_id: taskId || null, sub_task_id: subTaskId || null, entry_id: id });
      else await api.post('/procedures/assignments', { task_id: taskId || null, sub_task_id: subTaskId || null, procedure_id: id });
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) { toast({ title: 'Could not attach', status: 'error', duration: 2000 }); }
  };
  const detach = async (assignmentId) => {
    try {
      if (isPasswords) await api.delete(`/password-safe/assignments/${assignmentId}`);
      else await api.delete(`/procedures/assignments/${assignmentId}`);
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) {}
  };

  // ── Password actions ──
  const reveal = async (e) => {
    if (revealed[e.id] !== undefined) { setRevealed(p => { const n = { ...p }; delete n[e.id]; return n; }); return; }
    try { const { data } = await api.post(`/password-safe/${e.id}/reveal`); setRevealed(p => ({ ...p, [e.id]: data.password || '' })); }
    catch (err) { toast({ title: 'Could not reveal', status: 'error', duration: 2000 }); }
  };
  // Click the password (masked or revealed) → copy to clipboard.
  const copyPw = async (e) => {
    try {
      let pw = revealed[e.id];
      if (pw === undefined) { const { data } = await api.post(`/password-safe/${e.id}/reveal`); pw = data.password || ''; }
      await navigator.clipboard.writeText(pw);
      toast({ title: 'Password copied to clipboard', status: 'success', duration: 1200 });
    } catch (err) { toast({ title: 'Copy failed', status: 'error', duration: 2000 }); }
  };

  const openProcViewer = async (procId) => {
    try { const { data } = await api.get(`/procedures/${procId}`); setViewProc(data); } catch (e) {}
  };

  // Clone a procedure server-side (header + companies + steps) and attach
  // the copy here.
  const cloneProc = async (procId) => {
    try {
      const { data } = await api.post(`/procedures/${procId}/clone`);
      if (data?.id && !isCompanyMode) await attach(data.id);
      toast({ title: 'Procedure cloned' + (isCompanyMode ? '' : ' & attached'), description: 'Edit the copy in the Procedures dashboard.', status: 'success', duration: 2500 });
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) { toast({ title: 'Clone failed', status: 'error', duration: 2000 }); }
  };

  // Clone a password entry: pull its fields (password via the reveal
  // endpoint — visibility enforced) and prefill the New Entry form so the
  // copy can be renamed/rescoped before saving.
  const clonePw = async (entryId) => {
    try {
      const [{ data: det }, { data: rev }] = await Promise.all([
        api.get(`/password-safe/${entryId}`),
        api.post(`/password-safe/${entryId}/reveal`),
      ]);
      const f = emptyNewPw();
      f.name = `${det.name} (Copy)`;
      f.username = det.username || '';
      f.entry_type = det.entry_type === 'sso' ? 'sso' : 'account';
      f.sso_entry_id = det.sso_entry_id || '';
      f.password = f.entry_type === 'account' ? (rev.password || '') : '';
      f.url = det.url || '';
      f.notes = det.notes || '';
      if (isCompanyMode) { f.scope = 'company'; f.company_id = companyId; f.company_wide = true; }
      else if (ctx.companies.length > 0) f.company_id = ctx.companies[0].id;
      setNewPw(f); setShowPw(false);
    } catch (e) { toast({ title: 'Could not clone entry', status: 'error', duration: 2000 }); }
  };

  const openNewProc = () => {
    setNewProc({ name: '', description: '', category_id: '' });
    api.get('/procedures/categories').then(r => setProcCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  const saveNewProc = async () => {
    if (!newProc.name.trim()) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const companyIds = isCompanyMode ? [companyId] : ctx.companies.map(c => c.id);
      const { data } = await api.post('/procedures', {
        name: newProc.name, description: newProc.description,
        category_id: newProc.category_id || null, company_ids: companyIds,
      });
      if (data?.id && !isCompanyMode) {
        await api.post('/procedures/assignments', { task_id: taskId || null, sub_task_id: subTaskId || null, procedure_id: data.id }).catch(() => {});
      }
      toast({ title: 'Procedure created' + (isCompanyMode ? '' : ' & attached'), description: 'Add its steps from the Procedures dashboard.', status: 'success', duration: 3000 });
      setNewProc(null);
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  // ── Create new entry in context ──
  const openNewPw = () => {
    const f = emptyNewPw();
    if (isCompanyMode) { f.scope = 'company'; f.company_id = companyId; f.company_wide = true; }
    else if (ctx.companies.length > 0) { f.company_id = ctx.companies[0].id; }
    if (!isCompanyMode && !ctx.project_id && ctx.companies.length === 0) {
      toast({ title: 'No project context found for this task', status: 'warning', duration: 2500 });
    }
    setNewPw(f); setShowPw(false);
  };

  // ── Edit an existing entry in context ──
  // Pulls the full record (grants included) and pre-fills the same form.
  // The password field starts blank: blank on save = keep the current one.
  const openEditPw = async (entry) => {
    try {
      const { data } = await api.get(`/password-safe/${entry.id}`);
      const f = emptyNewPw();
      f.editing_id = data.id;
      f.name = data.name || '';
      f.username = data.username || '';
      f.url = data.url || '';
      f.notes = data.notes || '';
      f.entry_type = data.entry_type === 'sso' ? 'sso' : 'account';
      f.sso_entry_id = data.sso_entry_id || '';
      f.company_id = data.company_id || '';
      f.company_name = data.company_name || '';
      f.scope = data.company_id ? 'company' : 'project';
      f.company_wide = !!data.company_id && !data.project_id;
      f.can_manage = data.can_manage ?? data.is_creator ?? false;
      f.pin_id = data.creator_id || user?.id || '';
      f.pin_name = data.creator_name || user?.display_name || user?.username || '';
      // Owner is pinned in the picker; their grant row is preserved server-side.
      f.user_ids = (data.granted_users || [])
        .filter(u => String(u.id) !== String(f.pin_id))
        .map(u => ({ id: u.id, name: u.display_name || u.username }));
      setNewPw(f); setShowPw(false);
    } catch (e) { toast({ title: 'Could not open entry', description: e?.response?.data?.error || '', status: 'error', duration: 2500 }); }
  };

  const saveEditPw = async () => {
    if (!newPw.name.trim()) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    if (newPw.entry_type === 'sso' && !newPw.sso_entry_id) { toast({ title: 'Choose the Single Sign-On account', status: 'warning', duration: 2000 }); return; }
    if (newPw.scope === 'company' && !newPw.company_id) { toast({ title: 'Choose the company', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = {
        name: newPw.name, username: newPw.username, url: newPw.url, notes: newPw.notes,
        entry_type: newPw.entry_type,
        sso_entry_id: newPw.entry_type === 'sso' ? newPw.sso_entry_id : null,
      };
      // Scope is only editable from a task/sub-task (where the project
      // context is known); in company mode the existing scope is kept.
      if (!isCompanyMode) {
        payload.company_id = newPw.scope === 'company' ? newPw.company_id : null;
        payload.project_id = newPw.scope === 'project' ? ctx.project_id
          : (newPw.company_wide ? null : ctx.project_id);
      }
      if (newPw.entry_type === 'account' && newPw.password) {
        payload.password = newPw.password;
        payload.quality = passwordQuality(newPw.password);
      }
      // Access list: owner only — leave it out entirely otherwise.
      if (newPw.can_manage) payload.user_ids = newPw.user_ids.map(u => u.id);
      await api.put(`/password-safe/${newPw.editing_id}`, payload);
      toast({ title: 'Password entry updated', status: 'success', duration: 1500 });
      setNewPw(null); setRevealed({});
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.error || '', status: 'error', duration: 2500 });
    } finally { setSaving(false); }
  };

  const saveNewPw = async () => {
    if (newPw.editing_id) return saveEditPw();
    if (!newPw.name.trim()) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    if (newPw.entry_type === 'account' && !newPw.password) { toast({ title: 'Password required', status: 'warning', duration: 2000 }); return; }
    if (newPw.entry_type === 'sso' && !newPw.sso_entry_id) { toast({ title: 'Choose the Single Sign-On account', status: 'warning', duration: 2000 }); return; }
    if (newPw.scope === 'company' && !newPw.company_id) { toast({ title: 'Choose the company', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      // Scope → column mapping (migration 162):
      //   project only            → project_id, no company
      //   company, this project   → company_id + project_id
      //   company-wide            → company_id only
      const payload = {
        name: newPw.name, username: newPw.username,
        password: newPw.entry_type === 'account' ? newPw.password : undefined,
        url: newPw.url, notes: newPw.notes,
        entry_type: newPw.entry_type,
        sso_entry_id: newPw.entry_type === 'sso' ? newPw.sso_entry_id : null,
        owner_type: 'user', owner_id: user?.id || null,   // principal owner defaults to creator
        quality: newPw.entry_type === 'account' ? passwordQuality(newPw.password) : undefined,
        user_ids: newPw.user_ids.map(u => u.id),
        company_id: newPw.scope === 'company' ? newPw.company_id : null,
        project_id: isCompanyMode ? null
          : (newPw.scope === 'project' ? ctx.project_id
             : (newPw.company_wide ? null : ctx.project_id)),
      };
      const { data } = await api.post('/password-safe', payload);
      // Attach to the task/sub-task it was created from.
      if (!isCompanyMode && data?.id) {
        await api.post('/password-safe/assignments', {
          task_id: taskId || null, sub_task_id: subTaskId || null, entry_id: data.id,
        }).catch(() => {});
      }
      toast({ title: 'Password entry created', status: 'success', duration: 1500 });
      setNewPw(null);
      await fetchAll(); if (onChanged) onChanged();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.error || '', status: 'error', duration: 2500 });
    } finally { setSaving(false); }
  };

  const q = newPw ? passwordQuality(newPw.password) : 0;
  const attachedIds = new Set(items.map(i => String(i.id)));
  // Companies offered for the scope picker: this project's companies plus,
  // when editing, the entry's own company if it is not one of them.
  const scopeCompanies = (newPw?.editing_id && newPw.company_id
      && !ctx.companies.some(c => String(c.id) === String(newPw.company_id)))
    ? [...ctx.companies, { id: newPw.company_id, name: newPw.company_name || 'Current company', is_primary: 0 }]
    : ctx.companies;
  const heading = title || (isPasswords ? '🔐 Password Safe' : '📋 Procedures');

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent {...smallModalProps}>
        <ModalHeader fontSize="md" py={3}>
          <HStack spacing={2}>
            {isPasswords ? <FiKey size={16} /> : <FiClipboard size={16} />}
            <Text>{heading}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex={1} overflowY="auto">
          {loading ? <Flex justify="center" py={8}><Spinner /></Flex> : isPasswords ? (
            /* ═══ PASSWORDS ═══ */
            newPw ? (
              /* ── Create / edit form ── */
              <VStack align="stretch" spacing={2.5}>
                {newPw.editing_id && (
                  <HStack spacing={2}>
                    <FiEdit2 size={12} />
                    <Text fontSize="11px" fontWeight="700">Editing entry</Text>
                    {newPw.can_manage
                      ? <Badge colorScheme="purple" fontSize="7px">you own this entry</Badge>
                      : <Badge colorScheme="gray" fontSize="7px">owner: {newPw.pin_name || '—'}</Badge>}
                  </HStack>
                )}
                <HStack spacing={2}>
                  <Input size="sm" placeholder="Entry name *" value={newPw.name}
                    onChange={e => setNewPw(p => ({ ...p, name: e.target.value }))} autoFocus />
                  <Input size="sm" placeholder="Username" value={newPw.username}
                    onChange={e => setNewPw(p => ({ ...p, username: e.target.value }))} />
                </HStack>
                {/* Credential type: Account (own password) or Single
                    Sign-On via another entry, e.g. sign in with Google. */}
                <HStack spacing={2}>
                  <Select size="sm" w="190px" value={newPw.entry_type}
                    onChange={e => setNewPw(p => ({ ...p, entry_type: e.target.value }))}>
                    <option value="account">Account</option>
                    <option value="sso">Single Sign-On</option>
                  </Select>
                  {newPw.entry_type === 'sso' && (
                    <Select size="sm" flex={1} value={newPw.sso_entry_id}
                      onChange={e => setNewPw(p => ({ ...p, sso_entry_id: e.target.value }))}>
                      <option value="">Signs in with…</option>
                      {allItems.filter(e2 => String(e2.id) !== String(newPw.editing_id || '')).map(e2 => (
                        <option key={e2.id} value={e2.id}>{e2.name}{e2.company_name ? ` — ${e2.company_name}` : ''}</option>
                      ))}
                    </Select>
                  )}
                </HStack>
                {newPw.entry_type === 'account' && (<>
                <HStack spacing={2}>
                  <InputGroup size="sm">
                    <Input type={showPw ? 'text' : 'password'}
                      placeholder={newPw.editing_id ? 'New password (leave blank to keep current)' : 'Password *'} value={newPw.password}
                      onChange={e => setNewPw(p => ({ ...p, password: e.target.value }))} autoComplete="new-password" />
                    <InputRightElement>
                      <IconButton size="xs" variant="ghost" aria-label="show"
                        icon={showPw ? <FiEyeOff /> : <FiEye />} onClick={() => setShowPw(s => !s)} />
                    </InputRightElement>
                  </InputGroup>
                  <Button size="sm" variant="outline" leftIcon={<FiRefreshCw size={11} />} flexShrink={0}
                    onClick={() => { setNewPw(p => ({ ...p, password: genPassword() })); setShowPw(true); }}>Generate</Button>
                </HStack>
                {newPw.password && (
                  <HStack spacing={2}>
                    <Progress value={q} size="xs" colorScheme={qualityColor(q)} flex={1} borderRadius="full" />
                    <Text fontSize="9px" color={`${qualityColor(q)}.600`} fontWeight="700">{q}/100</Text>
                  </HStack>
                )}
                </>)}
                {newPw.entry_type === 'sso' && (
                  <Text fontSize="9px" color="gray.400">
                    No password stored — reveal &amp; copy use the linked account (needs to be visible to the user too).
                  </Text>
                )}
                <Input size="sm" placeholder="URL (https://…)" value={newPw.url}
                  onChange={e => setNewPw(p => ({ ...p, url: e.target.value }))} />

                {/* Scope */}
                {!isCompanyMode && (
                  <Box border="1px" borderColor="gray.200" borderRadius="md" p={2}>
                    <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Assign to</Text>
                    <RadioGroup value={newPw.scope} onChange={v => setNewPw(p => ({ ...p, scope: v }))}>
                      <Stack direction="row" fontSize="12px" spacing={4}>
                        <Radio value="project" size="sm">This project only{ctx.project_name ? ` (${ctx.project_name})` : ''}</Radio>
                        <Radio value="company" size="sm" isDisabled={scopeCompanies.length === 0}>Company</Radio>
                      </Stack>
                    </RadioGroup>
                    {newPw.scope === 'company' && (
                      <VStack align="stretch" mt={2} spacing={1.5}>
                        {scopeCompanies.length > 1 ? (
                          <Select size="xs" value={newPw.company_id}
                            onChange={e => setNewPw(p => ({ ...p, company_id: e.target.value }))}>
                            {scopeCompanies.map(c => <option key={c.id} value={c.id}>{c.name}{c.is_primary ? ' (primary)' : ''}</option>)}
                          </Select>
                        ) : (
                          <Text fontSize="11px" color="gray.600">{scopeCompanies[0]?.name}</Text>
                        )}
                        <Checkbox size="sm" isChecked={newPw.company_wide}
                          onChange={e => setNewPw(p => ({ ...p, company_wide: e.target.checked }))}>
                          <Text fontSize="11px">Available to <b>all projects</b> with this company (otherwise this project only)</Text>
                        </Checkbox>
                      </VStack>
                    )}
                  </Box>
                )}

                {/* Access — dual list */}
                <Box border="1px" borderColor="gray.200" borderRadius="md" p={2}>
                  <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                    Who can see this entry{newPw.editing_id && !newPw.can_manage ? ' — only the owner can change this' : ''}
                  </Text>
                  <UserTransferList users={users} value={newPw.user_ids}
                    onChange={(v) => setNewPw(p => ({ ...p, user_ids: v }))}
                    selfId={newPw.editing_id ? newPw.pin_id : user?.id}
                    selfName={newPw.editing_id ? (newPw.pin_name || 'Owner') : (user?.display_name || user?.username || 'You')}
                    disabled={!!newPw.editing_id && !newPw.can_manage} />
                  <Text fontSize="8px" color="gray.400" mt={1}>Users not on the right cannot see this entry exists.</Text>
                </Box>

                <Textarea size="sm" rows={2} placeholder="Notes" value={newPw.notes}
                  onChange={e => setNewPw(p => ({ ...p, notes: e.target.value }))} />
                <HStack justify="flex-end">
                  <Button size="sm" variant="ghost" onClick={() => setNewPw(null)}>Cancel</Button>
                  <Button size="sm" colorScheme="purple" onClick={saveNewPw} isLoading={saving}>
                    {newPw.editing_id ? 'Save Changes' : 'Create Entry'}
                  </Button>
                </HStack>
              </VStack>
            ) : (
              /* ── Entry list ── */
              <VStack align="stretch" spacing={1.5}>
                {items.length === 0 && (
                  <Text fontSize="11px" color="gray.400">
                    No password entries visible to you {isCompanyMode ? 'for this company' : 'attached here'} yet.
                  </Text>
                )}
                {items.map(e => (
                  <Flex key={e.assignment_id || e.id} border="1px" borderColor="gray.200" borderRadius="md" p={2} align="center" gap={2} flexWrap="wrap">
                    <FiKey size={13} />
                    <Box minW="130px">
                      <Text fontSize="12px" fontWeight="600">{e.name}</Text>
                      <Text fontSize="9px" color="gray.500">{e.username || '—'}{e.company_name ? ` · ${e.company_name}` : ''}{e.project_name ? ` · ${e.project_name}` : ''}</Text>
                      <HStack spacing={1}>
                        {e.entry_type === 'sso' && <Badge colorScheme="cyan" fontSize="7px">SSO → {e.sso_entry_name || 'linked'}</Badge>}
                        {!!e.can_manage && <Badge colorScheme="purple" variant="subtle" fontSize="7px">owner</Badge>}
                      </HStack>
                    </Box>
                    <Box flex={1} minW="110px">
                      <Tooltip label="Click to copy" fontSize="xs">
                        {revealed[e.id] !== undefined
                          ? <Text fontSize="11px" fontFamily="mono" bg="yellow.50" px={2} borderRadius="md"
                              display="inline-block" cursor="pointer" onClick={() => copyPw(e)}>{revealed[e.id] || '(empty)'}</Text>
                          : <Text fontSize="11px" color="gray.400" letterSpacing="2px" cursor="pointer"
                              display="inline-block" onClick={() => copyPw(e)}>••••••••</Text>}
                      </Tooltip>
                    </Box>
                    <HStack spacing={1}>
                      {e.url && <Tooltip label="Open URL"><IconButton size="xs" variant="ghost" icon={<FiExternalLink size={11} />}
                        aria-label="url" as="a" href={e.url} target="_blank" rel="noopener noreferrer" /></Tooltip>}
                      <Tooltip label={revealed[e.id] !== undefined ? 'Hide' : 'Reveal'}>
                        <IconButton size="xs" variant="ghost" icon={revealed[e.id] !== undefined ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                          aria-label="reveal" onClick={() => reveal(e)} /></Tooltip>
                      <Tooltip label={e.can_manage ? 'Edit entry, password and who can see it' : 'Edit entry (access list is owner-only)'}>
                        <IconButton size="xs" variant="ghost" colorScheme="purple" icon={<FiEdit2 size={11} />}
                          aria-label="edit" onClick={() => openEditPw(e)} /></Tooltip>
                      {!isCompanyMode && (
                        <Tooltip label="Detach from this task (the entry itself is kept)"><IconButton size="xs" variant="ghost" colorScheme="red"
                          icon={<FiTrash2 size={11} />} aria-label="detach" onClick={() => detach(e.assignment_id)} /></Tooltip>
                      )}
                    </HStack>
                  </Flex>
                ))}
                {/* Find existing (attach directly or clone) / Add New */}
                <Box pt={2} borderTop="1px" borderColor="gray.100">
                  <HStack spacing={2} mb={1}>
                    {!isCompanyMode && (
                      <Input size="xs" maxW="260px" placeholder="Search existing entries…" value={findQ}
                        onChange={e => setFindQ(e.target.value)} />
                    )}
                    <Button size="xs" colorScheme="purple" variant="outline" leftIcon={<FiPlus size={11} />}
                      onClick={openNewPw}>Add New</Button>
                  </HStack>
                  {!isCompanyMode && findQ.trim() && (
                    <VStack align="stretch" spacing={0.5} maxH="150px" overflowY="auto">
                      {allItems
                        .filter(p => !attachedIds.has(String(p.id)))
                        .filter(p => {
                          const q = findQ.trim().toLowerCase();
                          return (p.name || '').toLowerCase().includes(q)
                            || (p.username || '').toLowerCase().includes(q)
                            || (p.company_name || '').toLowerCase().includes(q);
                        }).slice(0, 8)
                        .map(p => (
                          <Flex key={p.id} px={2} py={1} bg="gray.50" borderRadius="md" align="center" gap={2} fontSize="11px">
                            <FiKey size={11} />
                            <Text flex={1} minW={0} isTruncated>{p.name}{p.company_name ? ` — ${p.company_name}` : ''}</Text>
                            <Button size="xs" variant="outline" colorScheme="purple" onClick={() => { attach(p.id); setFindQ(''); }}>Attach</Button>
                            <Tooltip label="Copy this entry as a new one you can edit"><Button size="xs" variant="ghost" onClick={() => clonePw(p.id)}>Clone</Button></Tooltip>
                          </Flex>
                        ))}
                    </VStack>
                  )}
                </Box>
              </VStack>
            )
          ) : (
            /* ═══ PROCEDURES ═══ */
            <VStack align="stretch" spacing={1.5}>
              {items.length === 0 && <Text fontSize="11px" color="gray.400">No procedures {isCompanyMode ? 'for this company' : 'attached'} yet.</Text>}
              {items.map(p => (
                <Flex key={p.assignment_id || p.id} border="1px" borderColor="gray.200" borderRadius="md" p={2} align="center" gap={2}>
                  <FiClipboard size={13} />
                  <Box flex={1} minW={0}>
                    <Text fontSize="12px" fontWeight="600" isTruncated>{p.name}</Text>
                    <HStack spacing={1}>
                      {p.category_name && <Badge colorScheme="teal" fontSize="7px">{p.category_name}</Badge>}
                      <Text fontSize="9px" color="gray.400">{p.step_count} step{p.step_count === 1 ? '' : 's'}</Text>
                    </HStack>
                  </Box>
                  <Button size="xs" variant="outline" colorScheme="teal" leftIcon={<FiEye size={11} />}
                    onClick={() => openProcViewer(p.id)}>View</Button>
                  <Tooltip label="Edit procedure (opens the Procedures page)"><IconButton size="xs" variant="ghost" colorScheme="teal"
                    icon={<FiEdit2 size={11} />} aria-label="edit"
                    onClick={() => {
                      // Proper page, not a nested modal — stacked modals
                      // don't scroll reliably.
                      onClose();
                      window.dispatchEvent(new CustomEvent('forgebos:open-procedures', { detail: { editId: p.id } }));
                    }} /></Tooltip>
                  {!isCompanyMode && (
                    <Tooltip label="Detach"><IconButton size="xs" variant="ghost" colorScheme="red"
                      icon={<FiTrash2 size={11} />} aria-label="detach" onClick={() => detach(p.assignment_id)} /></Tooltip>
                  )}
                </Flex>
              ))}
              {/* Find existing (attach or clone) / Add New */}
              {newProc ? (
                <Box border="1px" borderColor="teal.200" bg="teal.50" borderRadius="md" p={2}>
                  <VStack align="stretch" spacing={2}>
                    <Input size="sm" bg="white" placeholder="Procedure name *" value={newProc.name} autoFocus
                      onChange={e => setNewProc(pp => ({ ...pp, name: e.target.value }))} />
                    <Textarea size="sm" bg="white" rows={2} placeholder="Description" value={newProc.description}
                      onChange={e => setNewProc(pp => ({ ...pp, description: e.target.value }))} />
                    <Select size="sm" bg="white" value={newProc.category_id}
                      onChange={e => setNewProc(pp => ({ ...pp, category_id: e.target.value }))}>
                      <option value="">Category…</option>
                      {procCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                    <Text fontSize="9px" color="gray.500">
                      Created for {isCompanyMode ? 'this company' : "this project's companies"} and
                      {isCompanyMode ? '' : ' attached here'} — add its steps from the Procedures dashboard.
                    </Text>
                    <HStack justify="flex-end">
                      <Button size="xs" variant="ghost" onClick={() => setNewProc(null)}>Cancel</Button>
                      <Button size="xs" colorScheme="teal" onClick={saveNewProc} isLoading={saving}>Create</Button>
                    </HStack>
                  </VStack>
                </Box>
              ) : (
                <Box pt={2} borderTop="1px" borderColor="gray.100">
                  <HStack spacing={2} mb={1}>
                    <Input size="xs" maxW="280px" placeholder="Search existing procedures…" value={findQ}
                      onChange={e => setFindQ(e.target.value)} />
                    <Button size="xs" colorScheme="teal" variant="outline" leftIcon={<FiPlus size={11} />}
                      onClick={openNewProc}>Add New</Button>
                  </HStack>
                  {findQ.trim() && (
                    <VStack align="stretch" spacing={0.5} maxH="150px" overflowY="auto">
                      {allItems
                        .filter(p => !attachedIds.has(String(p.id)))
                        .filter(p => {
                          const q = findQ.trim().toLowerCase();
                          return (p.name || '').toLowerCase().includes(q)
                            || (p.description || '').toLowerCase().includes(q)
                            || (p.category_name || '').toLowerCase().includes(q)
                            || (p.companies || []).some(c => (c.name || '').toLowerCase().includes(q));
                        }).slice(0, 8)
                        .map(p => (
                          <Flex key={p.id} px={2} py={1} bg="gray.50" borderRadius="md" align="center" gap={2} fontSize="11px">
                            <FiClipboard size={11} />
                            <Text flex={1} minW={0} isTruncated>{p.name}{p.category_name ? ` (${p.category_name})` : ''}</Text>
                            {!isCompanyMode && (
                              <Button size="xs" variant="outline" colorScheme="teal" onClick={() => { attach(p.id); setFindQ(''); }}>Attach</Button>
                            )}
                            <Tooltip label="Duplicate this procedure (with all its steps) as an editable copy">
                              <Button size="xs" variant="ghost" onClick={() => { cloneProc(p.id); setFindQ(''); }}>Clone</Button>
                            </Tooltip>
                          </Flex>
                        ))}
                    </VStack>
                  )}
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter py={2}><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>

    {/* Non-blocking procedure pop-out viewer */}
    {viewProc && (
      <Modal isOpen={true} onClose={() => setViewProc(null)} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent maxH="85vh">
          <ModalHeader fontSize="md">📋 {viewProc.name}
            {viewProc.category_name && <Badge ml={2} colorScheme="teal" fontSize="9px">{viewProc.category_name}</Badge>}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" onWheel={e => e.stopPropagation()}><ProcedureViewer procedure={viewProc} /></ModalBody>
          <ModalFooter><Button size="sm" onClick={() => setViewProc(null)}>Close</Button></ModalFooter>
        </ModalContent>
      </Modal>
    )}
    </>
  );
}
